import { ref } from "vue";
import { useCountryISO3 } from "@/shared/common/composables/useCountryISO3";
import { useFetch } from "../api/common/composables/useFetch";
import { Logger } from "../utilities/logger";

const countryCodeISO2 = ref<string>("");
const countryCodeISO3 = ref<string>("");
const regionCode = ref<string>("");

/**
 * Parse the geolocation endpoint payload. Supports multiple shapes for a safe rollout:
 *  - an already-parsed JSON object `{ country: "CA", region: "QC" }` (`useFetch` auto-parses
 *    `application/json` responses instead of returning a string),
 *  - the new payload serialized as a JSON string `'{"country":"CA","region":"QC"}'`, and
 *  - the legacy bare country-code string (e.g. `"CA"`) returned before the backend was extended.
 */
function parseGeolocationResponse(raw: unknown): { country: string; region: string } {
  if (raw && typeof raw === "object") {
    const parsed = raw as { country?: string; region?: string };
    return { country: parsed.country || "", region: parsed.region || "" };
  }

  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) {
    return { country: "", region: "" };
  }
  if (value.startsWith("{")) {
    try {
      const parsed = JSON.parse(value) as { country?: string; region?: string };
      return { country: parsed.country || "", region: parsed.region || "" };
    } catch {
      return { country: "", region: "" };
    }
  }
  // Legacy bare country-code string (pre-region deploy): no region available.
  return { country: value, region: "" };
}

async function getCountryCode(hostname?: string) {
  try {
    if (sessionStorage.getItem("countryCodeISO2")) {
      countryCodeISO2.value = sessionStorage.getItem("countryCodeISO2") || "";
      countryCodeISO3.value = useCountryISO3(countryCodeISO2.value);
      regionCode.value = sessionStorage.getItem("regionCode") || "";
      return sessionStorage.getItem("countryCodeISO2") || "";
    }

    const { data } = await useFetch<{ country?: string; region?: string } | string>(hostname + "/api/geolocation");

    const { country, region } = parseGeolocationResponse(data.value);

    // Validate the country before trusting/caching it. The endpoint can occasionally return an
    // empty body or a non-ISO payload (e.g. an HTML error/SPA page); writing that into
    // sessionStorage would poison country detection for the whole session and silently hide
    // region-specific options like the German/French language selector.
    const normalizedCountry = country.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalizedCountry)) {
      Logger.error("Invalid country code received from geolocation:", data.value);
      return "";
    }

    countryCodeISO2.value = normalizedCountry;
    countryCodeISO3.value = useCountryISO3(normalizedCountry);

    // Region is best-effort (ISO 3166-2 subdivision, e.g. "QC"); an invalid/absent value simply
    // disables province-level auto-select without affecting country detection.
    const normalizedRegion = region.trim().toUpperCase();
    regionCode.value = /^[A-Z0-9]{1,3}$/.test(normalizedRegion) ? normalizedRegion : "";

    sessionStorage.setItem("countryCodeISO2", countryCodeISO2.value);
    sessionStorage.setItem("countryCodeISO3", countryCodeISO3.value);
    sessionStorage.setItem("regionCode", regionCode.value);

    return countryCodeISO2.value;
  } catch (error) {
    Logger.error("Failed to get country code:", error);
    throw error;
  }
}

export function useGeolocation() {
  return {
    getCountryCode,
    countryCodeISO2,
    countryCodeISO3,
    regionCode,
  };
}
